import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserProfile } from "../hooks/useUserProfile";
import {
    getProjectInfoList, refreshProjectInfo, getProjectInfoDetail,
    getExcludableProjects, setProjectExcluded, setAlertedProjects,
    getExcludableInvoicingProjects, setProjectExcludedFromInvoicing,
    createProjectMilestone, updateProjectMilestone, deleteProjectMilestone,
    updateProjectInfoManualFields, generateFinancialsReport,
    ProjectInfoSnapshot, ProjectInfoDetail as ProjectInfoDetailType, ExcludableProject, ProjectMilestoneInfo,
    ExcludableInvoicingProject,
} from "../services/api";

const AMPLIFY_ORANGE_LIGHT = "#FFF8EC"; // light tint of Amplify orange (#F18E00), alternate-row shade
const RED = "#C00000";
const RED_BG = "#F8D7DA"; // 2026-08-03: darkened a shade from the original #FDECEA per Pete's feedback
// Cell-color (not text-color) highlights:
const PURPLE_BG = "#EAE0F5"; // Remaining <= 10% of Budget (T&M only)
const ALERT_BLUE_BG = "#DCEAFB"; // admin-flagged "Alert" projects
const ALERT_BLUE_TEXT = "#0B5CAD"; // same blue family as ALERT_BLUE_BG, used for the Alert comment banner text

const isPoMissing = (po: string | null | undefined): boolean =>
    !po || !po.trim() || po.trim().toLowerCase() === "pending";

const isVacNegative = (p: ProjectInfoSnapshot): boolean =>
    p.vac != null && p.vac < 0;

// 2026-08-03, Pete's explicit correction: T&M only (Fixed Price has no
// Remaining-vs-Budget "runway" concept the way T&M does), and the
// threshold flips to Remaining being 10% or less of Budget (previously,
// incorrectly, "more than 90%").
const isRemainingHighPct = (p: ProjectInfoSnapshot): boolean =>
    p.project_type === "T&M" && p.budget != null && p.budget > 0 &&
    p.remaining != null && p.remaining <= 0.1 * p.budget;

// 2026-08-11 (Pete's explicit request): same purple used for the
// Remaining <= 10% condition, extended to the master list -- a project
// with any uninvoiced milestone past its Exp Inv Date shows purple here
// too, so it's visible without opening the project (the milestone table
// itself already highlights the specific row).
const isPurpleHighlighted = (p: ProjectInfoSnapshot): boolean =>
    isRemainingHighPct(p) || p.has_past_due_milestone;

// Cell-color (background) highlights. Three conditions: Alert
// (admin-flagged, blue), VAC negative (red), purple (Remaining <= 10% of
// Budget for T&M, or a past-due milestone for either type). When only one
// applies, all three cells (Project, Type, PO#) match it. 2026-08-04
// (v30), Pete's explicit split: when a project is BOTH Alert-flagged AND
// meets the red/purple criteria, the Project Name cell shows red/purple
// (whichever applies) while Type and PO# show blue instead -- so Alert is
// never fully masked by the financial highlight, or vice versa. PO#'s own
// missing/"pending" flag remains a separate, independent text-color
// (red/bold) treatment layered on top of whichever background applies.
const projectNameHighlightBg = (p: ProjectInfoSnapshot, baseColor: string): string => {
    if (isVacNegative(p)) return RED_BG;
    if (isPurpleHighlighted(p)) return PURPLE_BG;
    if (p.is_alerted) return ALERT_BLUE_BG;
    return baseColor;
};

const typePoHighlightBg = (p: ProjectInfoSnapshot, baseColor: string): string => {
    if (p.is_alerted) return ALERT_BLUE_BG;
    if (isVacNegative(p)) return RED_BG;
    if (isPurpleHighlighted(p)) return PURPLE_BG;
    return baseColor;
};

// Bold text whenever any of the three cell-background highlights above
// apply (2026-08-03, Pete's explicit request) -- independent of, and
// combinable with, PO#'s own missing/"pending" bold-red treatment.
const isHighlighted = (p: ProjectInfoSnapshot): boolean =>
    p.is_alerted || isVacNegative(p) || isPurpleHighlighted(p);

const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "8px 18px", cursor: "pointer", fontSize: "13px",
};
const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "8px 18px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "12px",
};
const thStyle: React.CSSProperties = {
    textAlign: "right", padding: "10px 12px", fontSize: "12px",
    color: "white", backgroundColor: "#5C5D5E", whiteSpace: "nowrap",
};
const thLeftStyle: React.CSSProperties = { ...thStyle, textAlign: "left" };
// 2026-08-06 (Pete's explicit request): "freeze panes" on the Project Info
// master list -- the header row stays pinned via position: sticky while
// the table body scrolls underneath it, inside that table's own
// bounded-height scroll container (see its wrapping div below). Only used
// on the master list's thead, not the Milestones/Timesheet Details tables
// on the detail page, which aren't in a bounded scroll container.
const stickyThStyle: React.CSSProperties = { ...thStyle, position: "sticky", top: 0, zIndex: 1 };
const stickyThLeftStyle: React.CSSProperties = { ...thLeftStyle, position: "sticky", top: 0, zIndex: 1 };
const tdStyle: React.CSSProperties = {
    textAlign: "right", padding: "9px 12px", fontSize: "13px",
    borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap",
};
const tdLeftStyle: React.CSSProperties = { ...tdStyle, textAlign: "left" };

// 2026-08-11 (Pete's explicit request): the backend stores/returns
// last_computed_at as a naive Postgres timestamp (UTC, no "Z"/offset
// suffix). The browser's Date constructor treats an ISO date-time string
// with no explicit offset as LOCAL time, not UTC -- so without this,
// "Last refreshed" was silently off by the viewer's UTC offset instead of
// showing their actual local time. Appending "Z" (only if not already
// present) tells Date it's UTC; toLocaleString() then renders it
// correctly in whatever time zone the browser itself is set to.
const formatServerTimestamp = (iso: string): string => {
    const hasZone = /[Zz]|[+-]\d\d:?\d\d$/.test(iso);
    return new Date(hasZone ? iso : `${iso}Z`).toLocaleString();
};

const money = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return "—";
    const neg = v < 0;
    const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return neg ? `($${s})` : `$${s}`;
};

const Spinner: React.FC = () => (
    <>
        <style>{"@keyframes pi-spin { to { transform: rotate(360deg); } }"}</style>
        <div style={{
            width: "14px", height: "14px", borderRadius: "50%",
            border: "2px solid #ffffff88", borderTopColor: "#fff",
            animation: "pi-spin 0.8s linear infinite", display: "inline-block",
        }} />
    </>
);

// ---------------------------------------------------------------------------
// Master view
// ---------------------------------------------------------------------------

const ProjectInfoMaster: React.FC = () => {
    const navigate = useNavigate();
    const { roles } = useUserProfile();
    const isAdmin = roles.includes("admin");

    const [projects, setProjects] = useState<ProjectInfoSnapshot[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<Record<string, string[]>>({});

    // Admin-only "Toggle View" (2026-08-06, Pete's explicit request):
    // admins default to seeing every active project, but can switch to the
    // same PM-only slice a project_manager-only user always sees, scoped to
    // their own identity. Non-admins never see this button -- they're
    // already always scoped to their own projects server-side.
    const [viewMode, setViewMode] = useState<"all" | "mine">("all");

    const [excludeMode, setExcludeMode] = useState(false);
    const [excludableProjects, setExcludableProjects] = useState<ExcludableProject[]>([]);
    const [excludeLoading, setExcludeLoading] = useState(false);
    const [excludeSaving, setExcludeSaving] = useState<Set<number>>(new Set());

    // "Exclude from Invoicing" mode (BACKLOG.md #18, 2026-09-10) -- same
    // separate full-screen instant-toggle pattern as Exclude above, but
    // independent of it: this flag only affects T&M Invoice Automation
    // (get_in_scope_projects()), not what shows on this Project Info screen.
    const [excludeInvoicingMode, setExcludeInvoicingMode] = useState(false);
    const [excludableInvoicingProjects, setExcludableInvoicingProjects] = useState<ExcludableInvoicingProject[]>([]);
    const [excludeInvoicingLoading, setExcludeInvoicingLoading] = useState(false);
    const [excludeInvoicingSaving, setExcludeInvoicingSaving] = useState<Set<number>>(new Set());

    // "Alert" mode -- a checkbox-then-Save workflow directly on the master
    // table (unlike Exclude mode's separate full-screen list with an
    // instant-toggle per row). Checking a project reveals a comment box
    // right below it (same pattern as the Rejection reason box on the
    // Approval Detail screen) -- the comment is saved alongside the flag
    // and shown as a banner on that project's detail screen.
    const [alertMode, setAlertMode] = useState(false);
    const [alertState, setAlertState] = useState<Record<number, { checked: boolean; comment: string }>>({});
    const [alertSaving, setAlertSaving] = useState(false);

    // Inline-editable ETC (T&M rows only -- Fixed Price's ETC is
    // auto-computed from milestones, never manually entered).
    const [etcEdits, setEtcEdits] = useState<Record<number, string>>({});
    const [etcSaving, setEtcSaving] = useState<Set<number>>(new Set());

    // 2026-08-04: no more auto-refresh-on-page-load. Data comes from
    // Postgres, kept current by a nightly job (same data both this screen
    // and Timesheet Validation read); withRefresh=true is now only ever
    // triggered by the explicit "Refresh" button below, for anyone who
    // needs it sooner than the next nightly pass -- including PM-only
    // users without access to the Zoho Sync config page.
    const load = useCallback(async (withRefresh: boolean) => {
        setError(null);
        // 2026-08-06 (Pete's explicit request): only show the full-page
        // "Loading..." state (which unmounts the table) on the very first
        // load. Every subsequent reload -- Refresh button, view toggle, or
        // the background re-fetch after saving an inline field like ETC --
        // keeps the existing table mounted so the page doesn't jump back to
        // the top mid-edit. See the `loading` gate below (now
        // `loading && projects.length === 0`) for the other half of this.
        if (withRefresh) setRefreshing(true); else setLoading(true);
        try {
            if (withRefresh) {
                const result = await refreshProjectInfo();
                setWarnings(result.warnings || {});
            }
            const data = await getProjectInfoList(viewMode);
            setProjects(data.projects);
        } catch (e) {
            console.error("Failed to load Project Info", e);
            setError("Failed to load Project Info. Please try again.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [viewMode]);

    useEffect(() => {
        load(false);
    }, [load]);

    // Latest last_computed_at across the currently-visible projects, shown
    // next to the Refresh button so it's clear how current the numbers are
    // under the new nightly-refresh model (2026-08-04).
    const lastRefreshedAt = projects.reduce<string | null>((latest, p) => {
        if (!p.last_computed_at) return latest;
        if (!latest || p.last_computed_at > latest) return p.last_computed_at;
        return latest;
    }, null);

    const openExcludeMode = async () => {
        setExcludeMode(true);
        setExcludeLoading(true);
        try {
            const data = await getExcludableProjects();
            setExcludableProjects(data.projects);
        } catch (e) {
            console.error("Failed to load excludable projects", e);
        } finally {
            setExcludeLoading(false);
        }
    };

    const toggleExcluded = async (projectId: number, currentlyExcluded: boolean) => {
        setExcludeSaving(prev => new Set(prev).add(projectId));
        try {
            await setProjectExcluded(projectId, !currentlyExcluded);
            setExcludableProjects(prev =>
                prev.map(p => p.project_id === projectId ? { ...p, is_excluded_from_view: !currentlyExcluded } : p)
            );
        } catch (e) {
            console.error("Failed to update exclude flag", e);
        } finally {
            setExcludeSaving(prev => {
                const next = new Set(prev);
                next.delete(projectId);
                return next;
            });
        }
    };

    const closeExcludeMode = () => {
        setExcludeMode(false);
        load(false);
    };

    const openExcludeInvoicingMode = async () => {
        setExcludeInvoicingMode(true);
        setExcludeInvoicingLoading(true);
        try {
            const data = await getExcludableInvoicingProjects();
            setExcludableInvoicingProjects(data.projects);
        } catch (e) {
            console.error("Failed to load invoicing-excludable projects", e);
        } finally {
            setExcludeInvoicingLoading(false);
        }
    };

    const toggleExcludedFromInvoicing = async (projectId: number, currentlyExcluded: boolean) => {
        setExcludeInvoicingSaving(prev => new Set(prev).add(projectId));
        try {
            await setProjectExcludedFromInvoicing(projectId, !currentlyExcluded);
            setExcludableInvoicingProjects(prev =>
                prev.map(p => p.project_id === projectId ? { ...p, is_excluded_from_invoicing: !currentlyExcluded } : p)
            );
        } catch (e) {
            console.error("Failed to update invoicing exclude flag", e);
        } finally {
            setExcludeInvoicingSaving(prev => {
                const next = new Set(prev);
                next.delete(projectId);
                return next;
            });
        }
    };

    const closeExcludeInvoicingMode = () => {
        setExcludeInvoicingMode(false);
    };

    const openAlertMode = () => {
        const seeded: Record<number, { checked: boolean; comment: string }> = {};
        projects.filter(p => p.is_alerted).forEach(p => {
            seeded[p.project_id] = { checked: true, comment: p.alert_comment || "" };
        });
        setAlertState(seeded);
        setAlertMode(true);
    };

    const cancelAlertMode = () => {
        setAlertMode(false);
        setAlertState({});
    };

    const toggleAlertSelection = (projectId: number) => {
        setAlertState(prev => {
            const current = prev[projectId];
            return {
                ...prev,
                [projectId]: { checked: !current?.checked, comment: current?.comment || "" },
            };
        });
    };

    const setAlertComment = (projectId: number, comment: string) => {
        setAlertState(prev => ({
            ...prev,
            [projectId]: { checked: prev[projectId]?.checked ?? true, comment },
        }));
    };

    const saveAlerts = async () => {
        setAlertSaving(true);
        try {
            const payload = Object.entries(alertState)
                .filter(([, v]) => v.checked)
                .map(([projectId, v]) => ({ project_id: Number(projectId), comment: v.comment.trim() }));
            await setAlertedProjects(payload);
            setAlertMode(false);
            setAlertState({});
            await load(false);
        } catch (e) {
            console.error("Failed to save Alert selections", e);
        } finally {
            setAlertSaving(false);
        }
    };

    const saveEtc = async (projectId: number) => {
        const raw = etcEdits[projectId];
        if (raw === undefined) return;
        const value = raw.trim() === "" ? null : parseFloat(raw);
        if (value !== null && Number.isNaN(value)) return;
        setEtcSaving(prev => new Set(prev).add(projectId));
        try {
            if (value !== null) {
                await updateProjectInfoManualFields(projectId, { etc: value });
            }
            setEtcEdits(prev => {
                const next = { ...prev };
                delete next[projectId];
                return next;
            });
            await load(false);
        } catch (e) {
            console.error("Failed to save ETC", e);
        } finally {
            setEtcSaving(prev => {
                const next = new Set(prev);
                next.delete(projectId);
                return next;
            });
        }
    };

    const warningCount = Object.values(warnings).reduce((sum, w) => sum + w.length, 0);

    if (loading && projects.length === 0) return <div>Loading Project Info...</div>;

    if (error) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error}</div>
                <button onClick={() => load(true)} style={secondaryBtnStyle}>Retry</button>
            </div>
        );
    }

    if (excludeMode) {
        return (
            <div>
                <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Exclude Projects</h1>
                <p style={{ color: "#666", marginTop: 0, marginBottom: "18px", fontSize: "13px" }}>
                    Excluded projects are hidden from the Project Info view for everyone. This has no effect on
                    timesheet entry, approvals, or Zoho sync.
                </p>
                {excludeLoading ? (
                    <div>Loading projects...</div>
                ) : (
                    <div style={{ border: "1px solid #e0e0e0", borderRadius: "6px", maxHeight: "480px", overflowY: "auto", backgroundColor: "white" }}>
                        {excludableProjects.map(p => (
                            <label key={p.project_id} style={{
                                display: "flex", alignItems: "center", gap: "10px",
                                padding: "9px 14px", fontSize: "13px", color: "#5C5D5E",
                                borderBottom: "1px solid #f0f0f0", cursor: "pointer",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={p.is_excluded_from_view}
                                    disabled={excludeSaving.has(p.project_id)}
                                    onChange={() => toggleExcluded(p.project_id, p.is_excluded_from_view)}
                                />
                                <span>{p.project_name}</span>
                                {excludeSaving.has(p.project_id) && <Spinner />}
                            </label>
                        ))}
                    </div>
                )}
                <div style={{ marginTop: "16px" }}>
                    <button onClick={closeExcludeMode} style={primaryBtnStyle}>Done</button>
                </div>
            </div>
        );
    }

    if (excludeInvoicingMode) {
        return (
            <div>
                <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Exclude from Invoicing</h1>
                <p style={{ color: "#666", marginTop: 0, marginBottom: "18px", fontSize: "13px" }}>
                    Excluded projects are skipped by T&M Invoice Automation (preflight, preview, and push) for
                    everyone. This has no effect on the Project Info view, timesheet entry, approvals, or Zoho sync.
                </p>
                {excludeInvoicingLoading ? (
                    <div>Loading projects...</div>
                ) : (
                    <div style={{ border: "1px solid #e0e0e0", borderRadius: "6px", maxHeight: "480px", overflowY: "auto", backgroundColor: "white" }}>
                        {excludableInvoicingProjects.map(p => (
                            <label key={p.project_id} style={{
                                display: "flex", alignItems: "center", gap: "10px",
                                padding: "9px 14px", fontSize: "13px", color: "#5C5D5E",
                                borderBottom: "1px solid #f0f0f0", cursor: "pointer",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={p.is_excluded_from_invoicing}
                                    disabled={excludeInvoicingSaving.has(p.project_id)}
                                    onChange={() => toggleExcludedFromInvoicing(p.project_id, p.is_excluded_from_invoicing)}
                                />
                                <span>{p.project_name}</span>
                                {excludeInvoicingSaving.has(p.project_id) && <Spinner />}
                            </label>
                        ))}
                    </div>
                )}
                <div style={{ marginTop: "16px" }}>
                    <button onClick={closeExcludeInvoicingMode} style={primaryBtnStyle}>Done</button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0, fontSize: "22px" }}>Project Info</h1>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {refreshing && <span style={{ fontSize: "12px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}><Spinner /> Refreshing…</span>}
                    {isAdmin && !alertMode && (
                        <button
                            onClick={() => setViewMode(prev => (prev === "all" ? "mine" : "all"))}
                            style={secondaryBtnStyle}
                        >
                            {viewMode === "all" ? "Show My Projects" : "Show All Projects"}
                        </button>
                    )}
                    <button onClick={() => load(true)} style={secondaryBtnStyle} disabled={refreshing}>Refresh</button>
                    {isAdmin && alertMode ? (
                        <>
                            <button onClick={cancelAlertMode} style={secondaryBtnStyle} disabled={alertSaving}>Cancel</button>
                            <button onClick={saveAlerts} style={{ ...primaryBtnStyle, opacity: alertSaving ? 0.6 : 1 }} disabled={alertSaving}>
                                {alertSaving ? "Saving…" : "Save"}
                            </button>
                        </>
                    ) : isAdmin && (
                        <>
                            <button onClick={openAlertMode} style={secondaryBtnStyle}>Alert</button>
                            <button onClick={openExcludeMode} style={secondaryBtnStyle}>Exclude</button>
                            <button onClick={openExcludeInvoicingMode} style={secondaryBtnStyle}>Exclude from Invoicing</button>
                        </>
                    )}
                </div>
            </div>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "18px", fontSize: "13px" }}>
                Budget, Actuals, Pending, and related figures for every active project. Data refreshes automatically
                overnight; click Refresh above for the latest. Click a project name to see full details.
                {alertMode && " Check the projects to highlight, then click Save."}
                {lastRefreshedAt && ` Last refreshed ${formatServerTimestamp(lastRefreshedAt)}.`}
            </p>

            {warningCount > 0 && (
                <div style={{
                    backgroundColor: "#FFF8EC", border: "1px solid #F18E00", borderRadius: "6px",
                    padding: "10px 14px", marginBottom: "16px", fontSize: "12px", color: "#5C5D5E",
                }}>
                    <strong>{warningCount} data-quality warning{warningCount !== 1 ? "s" : ""} on the last refresh:</strong>
                    <ul style={{ margin: "6px 0 0 0", paddingLeft: "18px" }}>
                        {Object.entries(warnings).map(([projectName, msgs]) => (
                            msgs.map((m, i) => <li key={`${projectName}-${i}`}>{projectName}: {m}</li>)
                        ))}
                    </ul>
                </div>
            )}

            {projects.length === 0 ? (
                <div style={{ color: "#999", fontSize: "13px" }}>No active projects found.</div>
            ) : (
                <div style={{
                    overflow: "auto", maxHeight: "70vh",
                    border: "1px solid #e0e0e0", borderRadius: "6px",
                }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        {/* 2026-08-06 (Pete's explicit request): "freeze panes" --
                            the header row stays pinned via position: sticky while
                            only the table body scrolls underneath it, within this
                            div's own bounded-height scroll region. Everything above
                            the table (title, Refresh/toggle buttons, description,
                            warnings banner) is already outside this scroll region
                            entirely, so it stays put by definition -- no separate
                            sticky treatment needed for it. */}
                        <thead>
                            <tr>
                                {alertMode && <th style={{ ...stickyThLeftStyle, width: "36px" }}></th>}
                                <th style={stickyThLeftStyle}>Project</th>
                                <th style={stickyThLeftStyle}>Type</th>
                                <th style={stickyThLeftStyle}>PO#</th>
                                <th style={stickyThStyle}>Budget</th>
                                <th style={stickyThStyle}>Actuals</th>
                                <th style={stickyThStyle}>Pending</th>
                                <th style={stickyThStyle}>Remaining</th>
                                <th style={stickyThStyle}>ETC</th>
                                <th style={stickyThStyle}>EAC</th>
                                <th style={stickyThStyle}>VAC</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((p, idx) => {
                                const isTM = p.project_type === "T&M";
                                const baseColor = idx % 2 === 1 ? AMPLIFY_ORANGE_LIGHT : "white";
                                const poMissing = isPoMissing(p.po_number);
                                const etcMissing = isTM && (p.etc === null || p.etc === undefined);
                                const etcValue = etcEdits[p.project_id] ?? (p.etc != null ? String(p.etc) : "");
                                const highlighted = isHighlighted(p);
                                const alertRow = alertState[p.project_id];
                                const totalCols = (alertMode ? 1 : 0) + 10;
                                return (
                                    <React.Fragment key={p.project_id}>
                                    <tr style={{ backgroundColor: baseColor }}>
                                        {alertMode && (
                                            <td style={{ ...tdLeftStyle, textAlign: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!alertRow?.checked}
                                                    onChange={() => toggleAlertSelection(p.project_id)}
                                                />
                                            </td>
                                        )}
                                        <td
                                            style={{
                                                ...tdLeftStyle,
                                                backgroundColor: projectNameHighlightBg(p, baseColor),
                                                fontWeight: highlighted ? "bold" : "normal",
                                                cursor: alertMode ? "default" : "pointer",
                                            }}
                                            onClick={alertMode ? undefined : () => navigate(`/reports/project-info/${p.project_id}`)}
                                        >
                                            {p.project_name}
                                        </td>
                                        <td style={{
                                            ...tdLeftStyle, backgroundColor: typePoHighlightBg(p, baseColor),
                                            fontWeight: highlighted ? "bold" : "normal",
                                        }}>{p.project_type}</td>
                                        <td style={{
                                            ...tdLeftStyle, backgroundColor: typePoHighlightBg(p, baseColor),
                                            color: poMissing ? RED : "inherit",
                                            fontWeight: (poMissing || highlighted) ? "bold" : "normal",
                                        }}>
                                            {p.po_number || "—"}
                                        </td>
                                        <td style={tdStyle}>{money(p.budget)}</td>
                                        <td style={tdStyle}>{money(p.actuals)}</td>
                                        <td style={tdStyle}>{money(p.pending)}</td>
                                        <td style={tdStyle}>{money(p.remaining)}</td>
                                        <td style={tdStyle}>
                                            {isTM ? (
                                                <input
                                                    type="number" step="0.01"
                                                    value={etcValue}
                                                    disabled={etcSaving.has(p.project_id)}
                                                    onChange={e => setEtcEdits(prev => ({ ...prev, [p.project_id]: e.target.value }))}
                                                    onBlur={() => saveEtc(p.project_id)}
                                                    onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                                    style={{
                                                        width: "90px", textAlign: "right", padding: "4px 6px", fontSize: "13px",
                                                        border: etcMissing ? `1px solid ${RED}` : "1px solid #ddd",
                                                        backgroundColor: etcMissing ? RED_BG : "white",
                                                        borderRadius: "4px",
                                                    }}
                                                />
                                            ) : money(p.etc)}
                                        </td>
                                        <td style={tdStyle}>{money(p.eac)}</td>
                                        <td style={tdStyle}>{money(p.vac)}</td>
                                    </tr>
                                    {alertMode && alertRow?.checked && (
                                        <tr style={{ backgroundColor: baseColor }}>
                                            <td colSpan={totalCols} style={{ ...tdLeftStyle, paddingTop: 0 }}>
                                                <textarea
                                                    value={alertRow.comment}
                                                    onChange={e => setAlertComment(p.project_id, e.target.value)}
                                                    placeholder="Alert comment (shown on this project's detail screen)"
                                                    disabled={alertSaving}
                                                    rows={2}
                                                    style={{
                                                        width: "100%", maxWidth: "500px", padding: "6px 8px",
                                                        fontSize: "13px", border: "1px solid #ddd",
                                                        borderRadius: "4px", boxSizing: "border-box",
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Project detail -- every project type navigates here now (2026-08-02).
// Metric tiles (same fields/order as the master table's columns) show for
// every project; Milestones + Margin are Fixed Price only; Timesheet
// Details (live per-line time-entry breakdown) is common to both, always
// rendered at the very bottom of the page.
// ---------------------------------------------------------------------------

const detailTileStyle: React.CSSProperties = {
    backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px",
    padding: "14px 20px", minWidth: "130px",
};
const detailTileLabelStyle: React.CSSProperties = { fontSize: "11px", color: "#999", textTransform: "uppercase" };
const detailTileValueStyle: React.CSSProperties = { fontSize: "18px", fontWeight: "bold", color: "#5C5D5E" };

const ProjectInfoDetail: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<ProjectInfoDetailType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [newDesc, setNewDesc] = useState("");
    const [newValue, setNewValue] = useState("");
    const [newExpInvDate, setNewExpInvDate] = useState("");
    const [adding, setAdding] = useState(false);

    // Milestone edit icon (2026-08-10 backlog item #1, extended 2026-08-11
    // per Pete's follow-up): inline-edit toggle per row for description/
    // value/Exp Inv Date, plus Invoice # and Invoice Date so a milestone
    // that was invoiced with a typo (or needs its date corrected) doesn't
    // require "Clear invoice" + re-invoice through the modal just to fix
    // it. The separate Mark Invoiced modal still exists as the normal way
    // to invoice a milestone for the first time.
    const [editingMilestoneId, setEditingMilestoneId] = useState<number | null>(null);
    const [editDesc, setEditDesc] = useState("");
    const [editValue, setEditValue] = useState("");
    const [editExpInvDate, setEditExpInvDate] = useState("");
    const [editInvoiceNumber, setEditInvoiceNumber] = useState("");
    const [editInvoiceDate, setEditInvoiceDate] = useState("");
    const [savingMilestoneEdit, setSavingMilestoneEdit] = useState(false);

    // Mark Invoiced modal (2026-08-06, Pete's explicit request): replaces
    // the old window.prompt("Invoice number:") with a real popup that also
    // captures Invoice Date, defaulted to today but overridable.
    const todayIso = () => new Date().toISOString().slice(0, 10);
    const [invoiceModalMilestoneId, setInvoiceModalMilestoneId] = useState<number | null>(null);
    const [invoiceNumberInput, setInvoiceNumberInput] = useState("");
    const [invoiceDateInput, setInvoiceDateInput] = useState(todayIso());
    const [savingInvoice, setSavingInvoice] = useState(false);

    const [laborHoursInput, setLaborHoursInput] = useState("");
    const [savingLaborHours, setSavingLaborHours] = useState(false);

    // Expenses (2026-08-04, Pete's explicit request) -- Expense Budget and
    // Expenses Used are manual carry-forward inputs, same editability rule
    // as ETC/Budgeted Labor Hours; Expenses Remaining is server-computed.
    const [expenseBudgetInput, setExpenseBudgetInput] = useState("");
    const [expensesUsedInput, setExpensesUsedInput] = useState("");
    const [savingExpenses, setSavingExpenses] = useState(false);

    // Generate Project Financials Report scoped to just this project
    // (2026-08-04, v30, T&M only per Pete's request) -- reuses the same
    // /reports/financials mode="projects" endpoint and blob-download
    // pattern as the full Project Financials page.
    const [generatingReport, setGeneratingReport] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getProjectInfoDetail(Number(projectId));
            setDetail(data);
            setLaborHoursInput(data.budgeted_labor_hours != null ? String(data.budgeted_labor_hours) : "");
            setExpenseBudgetInput(data.expense_budget != null ? String(data.expense_budget) : "");
            setExpensesUsedInput(data.expenses_used != null ? String(data.expenses_used) : "");
        } catch (e) {
            console.error("Failed to load project detail", e);
            setError("Failed to load project detail. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const handleAddMilestone = async () => {
        if (!projectId || !newDesc.trim() || !newValue || !newExpInvDate) return;
        setAdding(true);
        try {
            await createProjectMilestone(Number(projectId), {
                description: newDesc.trim(),
                value: parseFloat(newValue),
                exp_inv_date: newExpInvDate,
            });
            setNewDesc("");
            setNewValue("");
            setNewExpInvDate("");
            await load();
        } catch (e) {
            console.error("Failed to add milestone", e);
        } finally {
            setAdding(false);
        }
    };

    const openEditMilestone = (m: ProjectMilestoneInfo) => {
        setEditingMilestoneId(m.id);
        setEditDesc(m.description || "");
        setEditValue(m.value != null ? String(m.value) : "");
        setEditExpInvDate(m.exp_inv_date || "");
        setEditInvoiceNumber(m.invoice_number || "");
        setEditInvoiceDate(m.invoice_date || "");
    };

    const cancelEditMilestone = () => {
        setEditingMilestoneId(null);
    };

    const saveEditMilestone = async () => {
        if (editingMilestoneId === null || !editDesc.trim() || !editValue || !editExpInvDate) return;
        setSavingMilestoneEdit(true);
        try {
            await updateProjectMilestone(editingMilestoneId, {
                description: editDesc.trim(),
                value: parseFloat(editValue),
                exp_inv_date: editExpInvDate,
                // Only send Invoice #/Date if there's actually a value --
                // leaving both blank here shouldn't touch an existing
                // invoiced status one way or the other. Clearing an
                // invoice entirely is still done via the "Clear invoice"
                // button, not by blanking these out.
                ...(editInvoiceNumber.trim() ? { invoice_number: editInvoiceNumber.trim() } : {}),
                ...(editInvoiceDate ? { invoice_date: editInvoiceDate } : {}),
            });
            setEditingMilestoneId(null);
            await load();
        } catch (e) {
            console.error("Failed to save milestone edit", e);
        } finally {
            setSavingMilestoneEdit(false);
        }
    };

    const openMarkInvoicedModal = (milestoneId: number) => {
        setInvoiceModalMilestoneId(milestoneId);
        setInvoiceNumberInput("");
        setInvoiceDateInput(todayIso());
    };

    const closeMarkInvoicedModal = () => {
        setInvoiceModalMilestoneId(null);
    };

    const saveMarkInvoiced = async () => {
        if (invoiceModalMilestoneId === null || !invoiceNumberInput.trim()) return;
        setSavingInvoice(true);
        try {
            await updateProjectMilestone(invoiceModalMilestoneId, {
                invoice_number: invoiceNumberInput.trim(),
                invoice_date: invoiceDateInput || todayIso(),
            });
            setInvoiceModalMilestoneId(null);
            await load();
        } catch (e) {
            console.error("Failed to mark milestone invoiced", e);
        } finally {
            setSavingInvoice(false);
        }
    };

    const handleClearInvoice = async (milestoneId: number) => {
        try {
            await updateProjectMilestone(milestoneId, { clear_invoice: true });
            await load();
        } catch (e) {
            console.error("Failed to clear milestone invoice", e);
        }
    };

    const handleDeleteMilestone = async (milestoneId: number) => {
        if (!window.confirm("Delete this milestone?")) return;
        try {
            await deleteProjectMilestone(milestoneId);
            await load();
        } catch (e) {
            console.error("Failed to delete milestone", e);
        }
    };

    const handleSaveLaborHours = async () => {
        if (!projectId || laborHoursInput === "") return;
        setSavingLaborHours(true);
        try {
            await updateProjectInfoManualFields(Number(projectId), {
                budgeted_labor_hours: parseFloat(laborHoursInput),
            });
            await load();
        } catch (e) {
            console.error("Failed to save budgeted labor hours", e);
        } finally {
            setSavingLaborHours(false);
        }
    };

    const saveExpenseBudget = async () => {
        if (!projectId) return;
        const raw = expenseBudgetInput.trim();
        if (raw === "") return;
        const value = parseFloat(raw);
        if (Number.isNaN(value)) return;
        setSavingExpenses(true);
        try {
            await updateProjectInfoManualFields(Number(projectId), { expense_budget: value });
            await load();
        } catch (e) {
            console.error("Failed to save Expense Budget", e);
        } finally {
            setSavingExpenses(false);
        }
    };

    const saveExpensesUsed = async () => {
        if (!projectId) return;
        const raw = expensesUsedInput.trim();
        if (raw === "") return;
        const value = parseFloat(raw);
        if (Number.isNaN(value)) return;
        setSavingExpenses(true);
        try {
            await updateProjectInfoManualFields(Number(projectId), { expenses_used: value });
            await load();
        } catch (e) {
            console.error("Failed to save Expenses Used", e);
        } finally {
            setSavingExpenses(false);
        }
    };

    const handleGenerateReport = async () => {
        if (!projectId) return;
        setReportError(null);
        setGeneratingReport(true);
        try {
            const blob = await generateFinancialsReport({ mode: "projects", selected_ids: [Number(projectId)] });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const ts = new Date();
            const pad = (n: number) => n.toString().padStart(2, "0");
            const stamp = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`;
            a.href = url;
            a.download = `Project_Financials_${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Failed to generate report", e);
            setReportError("Failed to generate report. Please try again.");
        } finally {
            setGeneratingReport(false);
        }
    };

    // Same fix as the master list's loading gate: only show the full-page
    // loading state before we've ever loaded this project once, so that
    // milestone/expense saves' background re-fetch doesn't unmount the page
    // and reset scroll position (2026-08-06).
    if (loading && !detail) return <div>Loading...</div>;
    if (error || !detail) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error || "Not found."}</div>
                <button onClick={() => navigate("/reports/project-info")} style={secondaryBtnStyle}>← Back</button>
            </div>
        );
    }

    const milestones = detail.milestones || [];
    const budgetedLaborHours = detail.budgeted_labor_hours ?? null;
    const actualLaborHours = detail.actual_labor_hours ?? 0;
    const isFixed = detail.project_type === "Fixed";
    const timesheetDetails = detail.timesheet_details || [];
    const totalHours = timesheetDetails.reduce((sum, r) => sum + (r.hours || 0), 0);
    const totalCost = timesheetDetails.reduce((sum, r) => sum + (r.cost || 0), 0);

    // Same fields, same order, as the master table's summary columns
    // (Budget/Actuals/Pending/Remaining/ETC/EAC/VAC) -- Project/Type/PO#
    // are already covered by the header line above.
    const metricTiles: { label: string; value: number | null }[] = [
        { label: "Budget",    value: detail.budget },
        { label: "Actuals",   value: detail.actuals },
        { label: "Pending",   value: detail.pending },
        { label: "Remaining", value: detail.remaining },
        { label: "ETC",       value: detail.etc },
        { label: "EAC",       value: detail.eac },
        { label: "VAC",       value: detail.vac },
    ];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <button
                    onClick={() => navigate("/reports/project-info")}
                    style={secondaryBtnStyle}
                >
                    ← Back to Project Info
                </button>
                {!isFixed && (
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        {reportError && <span style={{ color: "#C00000", fontSize: "12px" }}>{reportError}</span>}
                        <button
                            onClick={handleGenerateReport}
                            disabled={generatingReport}
                            style={{ ...secondaryBtnStyle, opacity: generatingReport ? 0.6 : 1 }}
                        >
                            {generatingReport ? "Generating…" : "Generate Financials Report"}
                        </button>
                    </div>
                )}
            </div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 4px 0", fontSize: "22px" }}>{detail.project_name}</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "20px", fontSize: "13px" }}>
                {detail.customer_name || "—"} · {isFixed ? "Fixed Price" : "T&M"} · PO# {detail.po_number || "—"}
            </p>

            {detail.is_alerted && detail.alert_comment && (
                <div style={{
                    backgroundColor: ALERT_BLUE_BG, borderRadius: "4px",
                    padding: "12px 16px", marginBottom: "20px",
                }}>
                    <span style={{ color: ALERT_BLUE_TEXT, fontWeight: "bold", fontSize: "13px" }}>
                        {detail.alert_comment}
                    </span>
                </div>
            )}

            <div style={{ display: "flex", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
                {metricTiles.map(t => (
                    <div key={t.label} style={detailTileStyle}>
                        <div style={detailTileLabelStyle}>{t.label}</div>
                        <div style={detailTileValueStyle}>{money(t.value)}</div>
                    </div>
                ))}
            </div>

            {isFixed && (
            <>
            <h2 style={{ color: "#5C5D5E", fontSize: "16px", marginBottom: "10px" }}>Milestones</h2>
            <div style={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "16px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thLeftStyle}>#</th>
                            <th style={thLeftStyle}>Description</th>
                            <th style={thStyle}>Value</th>
                            <th style={thLeftStyle}>Exp Inv Date</th>
                            <th style={thLeftStyle}>Invoice #</th>
                            <th style={thLeftStyle}>Invoice Date</th>
                            <th style={thLeftStyle}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {milestones.length === 0 && (
                            <tr><td colSpan={7} style={{ ...tdLeftStyle, color: "#999" }}>No milestones yet.</td></tr>
                        )}
                        {milestones.map(m => {
                            const isEditing = editingMilestoneId === m.id;
                            // Backlog #3: past-due (uninvoiced + Exp Inv Date before
                            // today) highlights the first 3 fields purple, same
                            // convention/color as the master table's other purple
                            // highlight (Remaining <= 10% of Budget).
                            const pastDueCellStyle = m.is_past_due ? { backgroundColor: PURPLE_BG, fontWeight: "bold" as const } : {};
                            if (isEditing) {
                                return (
                                    <tr key={m.id} style={{ backgroundColor: "white" }}>
                                        <td style={tdLeftStyle}>{m.milestone_number}</td>
                                        <td style={tdLeftStyle}>
                                            <input
                                                type="text" value={editDesc}
                                                onChange={e => setEditDesc(e.target.value)}
                                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "100%" }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                type="number" value={editValue}
                                                onChange={e => setEditValue(e.target.value)}
                                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "110px" }}
                                            />
                                        </td>
                                        <td style={tdLeftStyle}>
                                            <input
                                                type="date" value={editExpInvDate}
                                                onChange={e => setEditExpInvDate(e.target.value)}
                                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px" }}
                                            />
                                        </td>
                                        <td style={tdLeftStyle}>
                                            <input
                                                type="text" value={editInvoiceNumber}
                                                placeholder="—"
                                                onChange={e => setEditInvoiceNumber(e.target.value)}
                                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "100px" }}
                                            />
                                        </td>
                                        <td style={tdLeftStyle}>
                                            <input
                                                type="date" value={editInvoiceDate}
                                                onChange={e => setEditInvoiceDate(e.target.value)}
                                                style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px" }}
                                            />
                                        </td>
                                        <td style={tdLeftStyle}>
                                            <button
                                                style={smallBtnStyle} onClick={saveEditMilestone}
                                                disabled={savingMilestoneEdit || !editDesc.trim() || !editValue || !editExpInvDate}
                                            >
                                                {savingMilestoneEdit ? "Saving…" : "Save"}
                                            </button>
                                            {" "}
                                            <button style={smallBtnStyle} onClick={cancelEditMilestone}>Cancel</button>
                                        </td>
                                    </tr>
                                );
                            }
                            return (
                                <tr key={m.id} style={{ backgroundColor: "white" }}>
                                    <td style={{ ...tdLeftStyle, ...pastDueCellStyle }}>{m.milestone_number}</td>
                                    <td style={{ ...tdLeftStyle, ...pastDueCellStyle }}>{m.description || "—"}</td>
                                    <td style={{ ...tdStyle, ...pastDueCellStyle }}>{money(m.value)}</td>
                                    <td style={tdLeftStyle}>{m.exp_inv_date || "—"}</td>
                                    <td style={tdLeftStyle}>{m.invoice_number || "—"}</td>
                                    <td style={tdLeftStyle}>{m.invoice_date || "—"}</td>
                                    <td style={tdLeftStyle}>
                                        <button style={smallBtnStyle} onClick={() => openEditMilestone(m)} title="Edit milestone">✎ Edit</button>
                                        {" "}
                                        {m.is_invoiced ? (
                                            <button style={smallBtnStyle} onClick={() => handleClearInvoice(m.id)}>Clear invoice</button>
                                        ) : (
                                            <button style={smallBtnStyle} onClick={() => openMarkInvoicedModal(m.id)}>Mark invoiced</button>
                                        )}
                                        {" "}
                                        <button style={smallBtnStyle} onClick={() => handleDeleteMilestone(m.id)}>Delete</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "32px" }}>
                <input
                    type="text" placeholder="Milestone description" value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    style={{ padding: "8px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", flex: 1 }}
                />
                <input
                    type="number" placeholder="Value" value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    style={{ padding: "8px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "140px" }}
                />
                <input
                    type="date" title="Exp Inv Date" value={newExpInvDate}
                    onChange={e => setNewExpInvDate(e.target.value)}
                    style={{ padding: "8px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "160px" }}
                />
                <button
                    onClick={handleAddMilestone}
                    disabled={adding || !newDesc.trim() || !newValue || !newExpInvDate}
                    style={{ ...primaryBtnStyle, opacity: (adding || !newDesc.trim() || !newValue || !newExpInvDate) ? 0.5 : 1 }}
                >
                    {adding ? "Adding…" : "Add Milestone"}
                </button>
            </div>

            <h2 style={{ color: "#5C5D5E", fontSize: "16px", marginBottom: "10px" }}>Margin</h2>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "14px 20px", minWidth: "160px" }}>
                    <div style={{ fontSize: "11px", color: "#999", textTransform: "uppercase" }}>Project Revenue</div>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#5C5D5E" }}>{money(detail.budget)}</div>
                </div>
                <div style={{ backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "14px 20px", minWidth: "160px" }}>
                    <div style={{ fontSize: "11px", color: "#999", textTransform: "uppercase" }}>Actual Labor Hours</div>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#5C5D5E" }}>{actualLaborHours.toLocaleString()}</div>
                </div>
                <div style={{ backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "14px 20px", minWidth: "180px" }}>
                    <div style={{ fontSize: "11px", color: "#999", textTransform: "uppercase", marginBottom: "6px" }}>Budgeted Labor Hours</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                            type="number" value={laborHoursInput}
                            onChange={e => setLaborHoursInput(e.target.value)}
                            style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "90px" }}
                        />
                        <button
                            onClick={handleSaveLaborHours}
                            disabled={savingLaborHours}
                            style={{ ...smallBtnStyle, opacity: savingLaborHours ? 0.5 : 1 }}
                        >
                            {savingLaborHours ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>
            {budgetedLaborHours == null && (
                <p style={{ color: "#999", fontSize: "12px", marginTop: "10px" }}>
                    Enter Budgeted Labor Hours to complete the margin picture for this project.
                </p>
            )}
            </>
            )}

            {/* Expenses (2026-08-04, Pete's explicit request). For T&M this
                lands right after the metric tiles above (the Milestones/
                Margin block just above is empty for T&M); for Fixed Price
                it lands after Milestones/Margin -- both cases end up
                immediately above Timesheet Details as specified. */}
            <h2 style={{ color: "#5C5D5E", fontSize: "16px", margin: isFixed ? "32px 0 10px 0" : "0 0 10px 0" }}>Expenses</h2>
            <div style={{ display: "flex", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
                <div style={detailTileStyle}>
                    <div style={detailTileLabelStyle}>Expense Budget</div>
                    <input
                        type="number" step="0.01"
                        value={expenseBudgetInput}
                        disabled={savingExpenses}
                        onChange={e => setExpenseBudgetInput(e.target.value)}
                        onBlur={saveExpenseBudget}
                        onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                        style={{
                            width: "100%", boxSizing: "border-box", border: "1px solid #ddd", borderRadius: "4px",
                            padding: "4px 6px", fontSize: "16px", fontWeight: "bold", color: "#5C5D5E",
                        }}
                    />
                </div>
                <div style={detailTileStyle}>
                    <div style={detailTileLabelStyle}>Expenses Used</div>
                    <input
                        type="number" step="0.01"
                        value={expensesUsedInput}
                        disabled={savingExpenses}
                        onChange={e => setExpensesUsedInput(e.target.value)}
                        onBlur={saveExpensesUsed}
                        onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                        style={{
                            width: "100%", boxSizing: "border-box", border: "1px solid #ddd", borderRadius: "4px",
                            padding: "4px 6px", fontSize: "16px", fontWeight: "bold", color: "#5C5D5E",
                        }}
                    />
                </div>
                <div style={detailTileStyle}>
                    <div style={detailTileLabelStyle}>Expenses Remaining</div>
                    <div style={detailTileValueStyle}>{money(detail.expenses_remaining)}</div>
                </div>
            </div>

            <h2 style={{ color: "#5C5D5E", fontSize: "16px", margin: "0 0 10px 0" }}>Timesheet Details</h2>
            <div style={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: "6px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thLeftStyle}>Consultant Name</th>
                            <th style={thLeftStyle}>Task</th>
                            <th style={thLeftStyle}>Date</th>
                            <th style={thStyle}>Hours</th>
                            <th style={thStyle}>Rate</th>
                            <th style={thStyle}>Cost</th>
                            <th style={thLeftStyle}>Comments</th>
                            <th style={thLeftStyle}>Invoice #</th>
                        </tr>
                    </thead>
                    <tbody>
                        {timesheetDetails.length === 0 && (
                            <tr><td colSpan={8} style={{ ...tdLeftStyle, color: "#999" }}>No time entries found for this project.</td></tr>
                        )}
                        {timesheetDetails.map((r, idx) => (
                            <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? AMPLIFY_ORANGE_LIGHT : "white" }}>
                                <td style={tdLeftStyle}>{r.consultant || "—"}</td>
                                <td style={tdLeftStyle}>{r.task || "—"}</td>
                                <td style={tdLeftStyle}>{r.date || "—"}</td>
                                <td style={tdStyle}>{r.hours.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td style={tdStyle}>{money(r.rate)}</td>
                                <td style={tdStyle}>{money(r.cost)}</td>
                                <td style={{ ...tdLeftStyle, whiteSpace: "normal" }}>{r.comments || "—"}</td>
                                <td style={tdLeftStyle}>{r.invoice_number || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                    {timesheetDetails.length > 0 && (
                        <tfoot>
                            <tr style={{ backgroundColor: "#f0f0f0", borderTop: "2px solid #ddd" }}>
                                <td style={{ ...tdLeftStyle, fontWeight: "bold" }} colSpan={3}>Total</td>
                                <td style={{ ...tdStyle, fontWeight: "bold" }}>
                                    {totalHours.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={tdStyle}></td>
                                <td style={{ ...tdStyle, fontWeight: "bold" }}>{money(totalCost)}</td>
                                <td style={tdLeftStyle}></td>
                                <td style={tdLeftStyle}></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {invoiceModalMilestoneId !== null && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.4)", display: "flex",
                        alignItems: "center", justifyContent: "center", zIndex: 100,
                    }}
                    onClick={closeMarkInvoicedModal}
                >
                    <div
                        style={{
                            backgroundColor: "white", borderRadius: "8px", padding: "24px 28px",
                            width: "340px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ margin: "0 0 16px 0", color: "#5C5D5E", fontSize: "16px" }}>Mark Milestone Invoiced</h3>
                        <div style={{ marginBottom: "14px" }}>
                            <label style={{ display: "block", fontSize: "12px", color: "#999", marginBottom: "4px", textTransform: "uppercase" }}>
                                Invoice #
                            </label>
                            <input
                                type="text"
                                autoFocus
                                value={invoiceNumberInput}
                                onChange={e => setInvoiceNumberInput(e.target.value)}
                                style={{
                                    width: "100%", boxSizing: "border-box", padding: "8px 10px",
                                    fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px",
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "20px" }}>
                            <label style={{ display: "block", fontSize: "12px", color: "#999", marginBottom: "4px", textTransform: "uppercase" }}>
                                Invoice Date
                            </label>
                            <input
                                type="date"
                                value={invoiceDateInput}
                                onChange={e => setInvoiceDateInput(e.target.value)}
                                style={{
                                    width: "100%", boxSizing: "border-box", padding: "8px 10px",
                                    fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px",
                                }}
                            />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button onClick={closeMarkInvoicedModal} style={secondaryBtnStyle} disabled={savingInvoice}>Cancel</button>
                            <button
                                onClick={saveMarkInvoiced}
                                disabled={savingInvoice || !invoiceNumberInput.trim()}
                                style={{ ...primaryBtnStyle, opacity: (savingInvoice || !invoiceNumberInput.trim()) ? 0.5 : 1 }}
                            >
                                {savingInvoice ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { ProjectInfoMaster, ProjectInfoDetail };
export default ProjectInfoMaster;
